import DescriptionIcon from '@mui/icons-material/Description';
import FolderIcon from '@mui/icons-material/Folder';
import Box from '@mui/material/Box';
import Paper from '@mui/material/Paper';
import Typography from '@mui/material/Typography';

import type { AppArcItem } from '@/api/generated';

import { UNAVAILABLE_CARD } from './arcPreview';

/** The card image's height in the frame, in CSS pixels. */
const IMAGE_HEIGHT = 140;

/** A page's header image, as its adapter puts it in `app` (`docs/mobile-api-arc.md`, section 4). */
function pageImage(card: AppArcItem): { url: string; alt: string } | null {
  const url = card.app.header_image_url;
  if (typeof url !== 'string' || !url) return null;
  const alt = card.app.header_image_alt;
  return { url, alt: typeof alt === 'string' && alt ? alt : card.title };
}

/**
 * One card as the app draws it (organization-arc, group 8; B19, D23): a page
 * with its header image, or the page icon when it has none; a document with
 * the document icon and its line (type, size, category); any other type
 * muted, because its module has not registered with the ARC yet, so the
 * app cannot open it. No card is a button or a link: Preview opens nothing
 * (D19).
 */
export function ArcPreviewCard({ card }: { card: AppArcItem }) {
  const image = card.type === 'PAGE' ? pageImage(card) : null;
  const openable = card.type === 'PAGE' || card.type === 'DOCUMENT';
  const Icon = card.type === 'DOCUMENT' ? FolderIcon : DescriptionIcon;
  return (
    <Paper
      component="li"
      variant="outlined"
      sx={{
        p: 1.5,
        display: 'grid',
        gap: 1,
        color: openable ? 'text.primary' : 'text.disabled',
      }}
      data-testid={`arc-preview-item-${card.id}-card`}
    >
      {image ? (
        <Box
          component="img"
          src={image.url}
          alt={image.alt}
          data-testid={`arc-preview-item-${card.id}-image`}
          sx={{
            display: 'block',
            width: '100%',
            height: IMAGE_HEIGHT,
            objectFit: 'cover',
            borderRadius: 1,
          }}
        />
      ) : null}
      <Box sx={{ display: 'flex', gap: 1, alignItems: 'flex-start', minWidth: 0 }}>
        {!image && openable ? (
          <Icon fontSize="small" aria-hidden sx={{ color: 'text.secondary', mt: 0.25 }} />
        ) : null}
        <Box sx={{ minWidth: 0 }}>
          <Typography
            variant="body2"
            sx={{ fontWeight: 600, overflowWrap: 'anywhere', color: 'inherit' }}
            data-testid={`arc-preview-item-${card.id}-title`}
          >
            {card.title}
          </Typography>
          {card.subtitle ? (
            <Typography
              variant="caption"
              component="p"
              color={openable ? 'text.secondary' : 'inherit'}
              sx={{ overflowWrap: 'anywhere' }}
              data-testid={`arc-preview-item-${card.id}-caption`}
            >
              {card.subtitle}
            </Typography>
          ) : null}
          {!openable ? (
            <Typography
              variant="caption"
              component="p"
              sx={{ color: 'inherit', fontStyle: 'italic' }}
              data-testid={`arc-preview-item-${card.id}-unavailable-label`}
            >
              {UNAVAILABLE_CARD}
            </Typography>
          ) : null}
        </Box>
      </Box>
    </Paper>
  );
}
